import styled from '@emotion/styled';
import { Button } from '@travelnest/hui';
import { Icon } from '@travelnest/merlin.icon';
import theme from '@travelnest/merlin.theme';
import { Paragraph } from '@travelnest/merlin.typography';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { MOBILE_BP } from '../../components/calendar/Calendar.styled';

import { sendCustomFullStoryEvent } from '../../../plugins';
import { P } from '../../../view/components/typography/P';

import { GradientInfoCard } from '../../../view/components/gradient-info-card/GradientInfoCard';

const Card = styled.div`
	border: 1px solid ${theme.colours.neutral.light};
	padding: ${theme.layout.spacing.large};
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: 8px;
`;

const ClickableCard = styled(Card)`
	width: 100%;
	&:hover {
		cursor: pointer;
		background: #fafafa;
		border: 1px solid #c1c1c1;
	}
	@media only screen and (min-width: ${MOBILE_BP}) {
		width: 50%;
	}

	p {
		color: ${theme.colours.neutral.darkest};
	}
`;

const IconWrapper = styled.div`
	width: 15px;
	height: 15px;
`;

const StatusLabel = styled.div`
	flex-grow: 1;
	text-align: right;
	padding-right: ${theme.layout.spacing.standard};
	color: #0f7a5d;
	font-size: 12px;
	line-height: initial;
	font-weight: 700;
`;

const TBP_MODE_PARAM = /\/(?<mode>[a-z-]+)\/tasks\/[a-z-]/;

export const getCurrentTBPMode = (currentPath: string) => {
	const match = currentPath.match(TBP_MODE_PARAM);
	if (!!match?.groups?.mode) {
		return match.groups.mode;
	} else {
		return null;
	}
};

export const SmartPricingCard: React.FC<{
	propertyId: string | number;
	smartPricingEnabled: boolean;
}> = ({ propertyId, smartPricingEnabled }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const tbpMode = getCurrentTBPMode(location.pathname) ?? 'edit';

	return smartPricingEnabled ? (
		<ClickableCard
			className="smart-pricing-clickable-card"
			tabIndex={0}
			role="button"
			onClick={() =>
				navigate(`/property/${propertyId}/${tbpMode}/tasks/pricing`)
			}
		>
			<Paragraph>Smart pricing</Paragraph>
			<StatusLabel>ON</StatusLabel>
			<IconWrapper>
				<Icon icon="right" />
			</IconWrapper>
		</ClickableCard>
	) : (
		<GradientInfoCard className="smart-pricing-gradient-card">
			<h3>Smart pricing</h3>
			<P>
				Save time and enjoy effortless pricing. Earn{' '}
				<b>up to 60% more</b> in revenue by automatically adjusting
				prices according to local market trends.
			</P>
			<Button
				text="Go to pricing"
				variant="positive"
				fill="light"
				style={{ marginTop: '8px' }}
				onClick={() => {
					sendCustomFullStoryEvent(
						'smart_pricing_calendar_card_button_clicked',
						{
							property_id: propertyId,
						}
					);
					navigate(
						`/property/${propertyId}/${tbpMode}/tasks/pricing`
					);
				}}
			/>
		</GradientInfoCard>
	);
};
